import { Component, Input, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { DateUtilService, OptionsList } from 'gung-common';
import { debounce, first, interval } from 'rxjs';
import { AuthService } from '../../../services/auth/auth.service';
import {
  DiscountCodeV2,
  DiscountCodeWriteExpressionTemplate,
  DiscountCodesV2Service
} from '../../../services/discount-codes-v2/discount-codes-v2.service';
import { format } from 'date-fns';

@Component({
  selector: 'lib-discount-codes-modal',
  templateUrl: './discount-codes-modal.component.html',
  styleUrls: ['./discount-codes-modal.component.css'],
  standalone: false
})
export class DiscountCodesModalComponent implements OnInit {
  @Input()
  discountCode: DiscountCodeV2;
  discountCodeMongo: DiscountCodeV2;

  submitForm: FormGroup;
  disabled: boolean;

  minDate = new Date();

  writeExpressions: DiscountCodeWriteExpressionTemplate[];
  conditionExpressions = [];
  isActuator = false;
  disableDiscountConditions = true;

  activeWriteExpression: DiscountCodeWriteExpressionTemplate;
  showExpression = false;

  get logicalConditions() {
    return this.submitForm.get('discountCodeConditions').get('logicalConditions') as FormArray;
  }

  matchingOptionsList: OptionsList[] = [
    {
      id: 'AND',
      name: 'MATCH_ALL_CONDITIONS'
    },
    {
      id: 'OR',
      name: 'MATCH_AT_LEAST_ONE_CONDITION'
    }
  ];
  logicalConditionsOptionsList: OptionsList[] = [];
  tempInputForExpressionValues: string[] = [];

  constructor(
    protected formBuilder: FormBuilder,
    public activeModal: NgbActiveModal,
    protected discountCodesV2Service: DiscountCodesV2Service,
    protected authService: AuthService,
    public dateUtilService: DateUtilService
  ) { }

  ngOnInit(): void {
    this.authService
      .getHasRoles('ACTUATOR')
      .pipe(first())
      .subscribe(isActuator => (this.isActuator = isActuator));
    this.writeExpressions = this.discountCodesV2Service.writeExpressions;
    this.logicalConditionsOptionsList = this.discountCodesV2Service.logicalConditionsOptionsList;
    // if (this.discountCode) {
    //   this.discountCodeMongo = JSON.parse(JSON.stringify(this.discountCode));
    // }
    this.initForm();
    this.generateMongoObject();
    this.submitForm.valueChanges.pipe(debounce(() => interval(250))).subscribe(x => {
      this.generateMongoObject();
    });
  }

  initForm() {
    this.submitForm = this.formBuilder.group({
      code: this.formBuilder.control(
        { value: this.discountCode?.code || '', disabled: this.discountCode ? true : false },
        [Validators.required]
      ),
      description: this.formBuilder.control(this.discountCode?.description || ''),
      // usedByCompanyForOrderMap: this.formBuilder.control(this.discountCode?.usedByCompanyForOrderMap || this.discountCodeMongo?.usedByCompanyForOrderMap || {}),
      createdDate: this.formBuilder.control(this.discountCode?.createdDate || new Date()),
      validFrom: this.formBuilder.control(
        format(this.discountCode?.validFrom ? new Date(this.discountCode.validFrom) : new Date(), "yyyy-MM-dd'T'HH:mm"),
        [Validators.required]
      ),
      validUntil: this.formBuilder.control(
        format(this.discountCode?.validUntil ? new Date(this.discountCode.validUntil) : new Date(), "yyyy-MM-dd'T'HH:mm"),
        [Validators.required]
      ),
      maxMultipleUses: this.formBuilder.control(this.discountCode?.maxMultipleUses || 0, [
        Validators.required,
        Validators.min(0)
      ]),
      writeExpression: this.formBuilder.control(this.discountCode?.writeExpression || '', [Validators.required]),
      discountAmount: this.formBuilder.control(this.discountCode?.discountAmount || '', [Validators.required, Validators.min(0)]),
      orderRowDiscount: this.formBuilder.control(this.discountCode?.orderRowDiscount || false),
      addsNewRow: this.formBuilder.control({ value: this.discountCode?.addsNewRow || false, disabled: true })
    });

    this.enableDiscountCodeConditions(this.discountCode?.orderRowDiscount, this.discountCode);
    this.enableProductId(this.discountCode?.addsNewRow);

    this.activeWriteExpression = this.writeExpressions.find(w => w.expression === this.discountCode?.writeExpression);
  }

  generateMongoObject() {
    const today = format(new Date(), "yyyy-MM-dd'T'00:00");
    const formRaw = this.submitForm.getRawValue();
    this.discountCodeMongo = {
      code: formRaw.code || '',
      description: formRaw.description || '',
      // usedByCompanyForOrderMap: this.discountCode?.usedByCompanyForOrderMap || formRaw.usedByCompanyForOrderMap || {},
      createdDate: formRaw.createdDate || new Date(),
      validFrom: formRaw.validFrom || today,
      validUntil: formRaw.validUntil || today,
      maxMultipleUses: formRaw.maxMultipleUses || 0,
      // extra: this.discountCode?.extra || formRaw.extra || {oh: {ordrabatt: 0}},
      writeExpression: formRaw.writeExpression || '',
      discountAmount: formRaw.discountAmount || 0,
      orderRowDiscount: formRaw.orderRowDiscount || false,
      discountCodeConditions: formRaw.discountCodeConditions || undefined
    };
  }

  saveMongoObject(event) {
    this.discountCodeMongo = JSON.parse(event);
    this.submitForm = this.formBuilder.group({
      code: this.formBuilder.control(this.discountCodeMongo?.code || '', [Validators.required]),
      description: this.formBuilder.control({
        value: this.discountCodeMongo?.description || '',
        disabled: this.discountCode ? true : false
      }),
      // usedByCompanyForOrderMap: this.formBuilder.control(this.discountCode?.usedByCompanyForOrderMap || this.discountCodeMongo?.usedByCompanyForOrderMap || {}),
      createdDate: this.formBuilder.control(this.discountCodeMongo?.createdDate || new Date()),
      validFrom: this.formBuilder.control(
        format(
          this.discountCodeMongo?.validFrom ? new Date(this.discountCodeMongo.validFrom) : new Date(),
          "yyyy-MM-dd'T'HH:mm"
        )
      ),
      validUntil: this.formBuilder.control(
        format(
          this.discountCodeMongo?.validUntil ? new Date(this.discountCodeMongo.validUntil) : new Date(),
          "yyyy-MM-dd'T'HH:mm"
        )
      ),
      maxMultipleUses: this.formBuilder.control(this.discountCodeMongo?.maxMultipleUses || 0, [
        Validators.required,
        Validators.min(0)
      ]),
      writeExpression: this.formBuilder.control(this.discountCodeMongo?.writeExpression || ''),
      discountAmount: this.formBuilder.control(this.discountCodeMongo?.discountAmount || ''),
      orderRowDiscount: this.formBuilder.control(this.discountCodeMongo?.orderRowDiscount || false),
      addsNewRow: this.formBuilder.control(this.discountCode?.addsNewRow || false)
    });

    this.enableDiscountCodeConditions(this.discountCodeMongo?.orderRowDiscount, this.discountCodeMongo);
    this.enableProductId(this.discountCode?.addsNewRow);
  }

  enableDiscountCodeConditions(enable: boolean, origin: DiscountCodeV2) {
    if (enable) {
      const logicalConditionsArray =
        origin?.discountCodeConditions?.logicalConditions?.map(condition =>
          this.formBuilder.group({
            conditionName: this.formBuilder.control(condition.conditionName),
            expressionString: this.formBuilder.control(condition.expressionString),
            expressionValues: this.formBuilder.array(condition.expressionValues),
            expressionValuesAllowed: this.formBuilder.control(condition.expressionValuesAllowed),
            type: this.formBuilder.control(condition.type)
          })
        ) || [];

      this.submitForm.addControl(
        'discountCodeConditions',
        this.formBuilder.group({
          logicalConditions: this.formBuilder.array(logicalConditionsArray),
          logicalOperator: this.formBuilder.control(origin?.discountCodeConditions?.logicalOperator || 'OR'),
          type: this.formBuilder.control(origin?.discountCodeConditions?.type || 'LogicalConditionChain')
        })
      );
    } else {
      this.submitForm.removeControl('discountCodeConditions');
    }
  }

  addLogicalCondition() {
    this.logicalConditions.push(
      this.formBuilder.group({
        conditionName: this.formBuilder.control(''),
        expressionString: this.formBuilder.control(''),
        expressionValues: this.formBuilder.array([]),
        expressionValuesAllowed: this.formBuilder.control(false),
        type: this.formBuilder.control('LogicalConditionExpression')
      })
    );
  }

  removeCondition(i) {
    this.logicalConditions.removeAt(i);
  }

  addMultipleValues(index: number) {
    const logicalCondition = this.logicalConditions.at(index) as FormGroup;
    const expressionValuesArray = logicalCondition.get('expressionValues') as FormArray;

    if (this.tempInputForExpressionValues[index].length > 0) {
      const newValues = this.tempInputForExpressionValues[index]
        .split(',')
        .map(value => value.trim())
        .filter(value => value && !expressionValuesArray.value.includes(value));

      newValues.forEach(value => expressionValuesArray.push(this.formBuilder.control(value)));
    }
  }

  onInputChange(value: string, index: number) {
    if (index >= this.tempInputForExpressionValues.length) {
      this.tempInputForExpressionValues.length = index + 1;
    }
    this.tempInputForExpressionValues[index] = value;
  }

  clearValues(index) {
    const condition = this.logicalConditions.at(index) as FormGroup;
    const expressionValues = condition.get('expressionValues') as FormArray;
    while (expressionValues.length) {
      expressionValues.removeAt(0);
    }
  }

  clearSingleValue(index, valueIndex) {
    const condition = this.logicalConditions.at(index) as FormGroup;
    const expressionValues = condition.get('expressionValues') as FormArray;
    expressionValues.removeAt(valueIndex);
  }

  enableProductId(enable) {
    if (enable) {
      this.submitForm.addControl(
        'productId',
        this.formBuilder.control(this.discountCode?.productId || '', [Validators.required])
      );
      this.submitForm.get('discountAmount').addValidators([Validators.required]);
    } else {
      this.submitForm.removeControl('productId');
      this.submitForm.get('discountAmount').removeValidators([Validators.required]);
    }
  }

  onSave() {
    this.submitForm.markAllAsTouched();
    if (this.submitForm.invalid) {
      // FILL_ALL_REQUIRED_FIELDS
      return;
    }
    this.disabled = true;
    const formRaw = this.submitForm.getRawValue();

    let discountCode: DiscountCodeV2 = {
      ...formRaw
    };
    discountCode = {
      ...discountCode,
      validFrom: new Date(discountCode.validFrom),
      validUntil: new Date(discountCode.validUntil)
    };
    // Update
    if (this.discountCode?.code) {
      // discountCode.code = this.discountCode.code;
      this.discountCodesV2Service.putDiscountCodes(discountCode).subscribe(ticket => {
        this.disabled = false;
        this.activeModal.close(JSON.stringify(ticket));
      });
    } else {
      this.discountCodesV2Service.postDiscountCodes(discountCode).subscribe(ticket => {
        this.disabled = false;
        this.activeModal.close(JSON.stringify(ticket));
      });
    }
  }

  writeExpressionChange(writeExpression) {
    const foundWriteExpression = this.writeExpressions.find(w => w.expression === writeExpression);
    this.activeWriteExpression = foundWriteExpression;
    this.submitForm.get('orderRowDiscount').setValue(!!foundWriteExpression?.orderRowDiscount);
    this.submitForm.get('addsNewRow').setValue(!!foundWriteExpression?.addsNewRow);

    this.enableDiscountCodeConditions(foundWriteExpression.orderRowDiscount, this.discountCode);
    this.enableProductId(foundWriteExpression?.addsNewRow);
  }
}
